import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MobileLayout from '../layout/MobileLayout';
import MobileBottomNav from '../layout/MobileBottomNav';
import { LoadingSpinner } from '../common/LoadingSpinner';
import Button from '../common/Button';
import { useAuth } from '../../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/payments/packages`);
      // The backend returns {packages: {free: {...}, pro: {...}}}
      const packagesData = response.data.packages;
      // Convert to array format for easier rendering
      const packagesArray = Object.keys(packagesData).map(key => ({
        id: key,  // Use 'id' instead of 'name' to avoid confusion
        name: key,
        ...packagesData[key]
      }));
      
      console.log('Processed packages:', packagesArray); // Debug log
      setPackages(packagesArray);
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (packageId) => {
    setProcessingPayment(true);
    
    try {
      // Use the proper origin URL from environment variable
      const originUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;
      
      const response = await axios.post(`${API}/payments/create-checkout`, {
        package_id: packageId,
        origin_url: originUrl
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Redirect to Stripe checkout
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received from server');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      let errorMessage = 'Failed to create checkout session. Please try again.';
      
      if (error.response?.data?.detail) {
        errorMessage = `Payment Error: ${error.response.data.detail}`;
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication required. Please log in and try again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Permission denied. Please check your subscription status.';
      }
      
      alert(errorMessage);
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <MobileLayout title="Upgrade" subtitle="Choose your plan">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
        <MobileBottomNav />
      </MobileLayout>
    );
  }

  const currentPlan = user?.subscription_plan || 'free';
  const isSubscribed = (user?.subscription_status === 'active' || user?.subscription_status === 'pro') && user?.subscription_plan === 'pro';

  return (
    <MobileLayout title="Upgrade" subtitle="Unlock premium features">
      {/* Current Status */}
      <div className="mobile-card p-6 mb-6">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Current Plan</h2>
          <div className={`inline-flex items-center px-4 py-2 rounded-full font-medium ${
            isSubscribed 
              ? 'bg-green-100 text-green-800' 
              : 'bg-gray-100 text-gray-800'
          }`}>
            {isSubscribed ? '✅ Pro Plan Active' : '🆓 Free Plan'}
          </div>
          {!isSubscribed && (
            <p className="text-gray-600 mt-3">
              You're currently on the free plan with limited features.
            </p>
          )}
        </div>
      </div>

      {/* Free Plan Limitations */}
      {!isSubscribed && (
        <div className="mobile-card p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <span className="text-yellow-500 mr-2">⚠️</span>
            Free Plan Limitations
          </h3>
          <ul className="space-y-2 text-gray-600">
            <li className="flex items-center">
              <span className="text-red-500 mr-2">✗</span>
              Limited to 3 contacts
            </li>
            <li className="flex items-center">
              <span className="text-red-500 mr-2">✗</span>
              100 MB storage limit
            </li>
            <li className="flex items-center">
              <span className="text-red-500 mr-2">✗</span>
              Basic task features only
            </li>
            <li className="flex items-center">
              <span className="text-red-500 mr-2">✗</span>
              No priority support
            </li>
          </ul>
        </div>
      )}

      {/* Pricing Plans */}
      {packages.length > 0 && (
        <div className="space-y-4 mb-6">
          {packages.filter(pkg => pkg.id !== 'free').map(pkg => (
            <div key={pkg.id} className="mobile-card p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 capitalize">{pkg.name === 'free' ? 'Free' : 'Pro'} Plan</h3>
                  {pkg.id === 'pro' && (
                    <span className="inline-block bg-blue-600 text-white text-xs px-2 py-1 rounded-full mt-1">
                      Most Popular
                    </span>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-blue-600">
                    ${pkg.amount}
                  </div>
                  <div className="text-sm text-gray-600">
                    per {pkg.duration}
                  </div>
                </div>
              </div>

              {/* Features List */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Features included:</h4>
                <ul className="space-y-2 text-gray-600">
                  {pkg.id === 'free' ? (
                    <>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Up to 3 contacts
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Basic task management
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        100 MB storage
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Email support
                      </li>
                    </>
                  ) : (
                    <>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Unlimited contacts
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Advanced task management
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        10 GB storage
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Calendar integration
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        File attachments
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Priority support
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Admin dashboard
                      </li>
                    </>
                  )}
                </ul>
              </div>

              {/* Action Button */}
              <Button
                fullWidth
                loading={processingPayment}
                disabled={processingPayment || isSubscribed}
                onClick={() => handleUpgrade(pkg.id === 'pro' ? 'pro' : 'free')}
              >
                {isSubscribed ? 'Already Subscribed' : `Upgrade to ${pkg.id === 'free' ? 'Free' : 'Pro'}`}
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Money Back Guarantee */}
      <div className="mobile-card p-6 mb-6 bg-green-50 border border-green-200">
        <div className="text-center">
          <div className="text-4xl mb-3">💰</div>
          <h3 className="font-semibold text-green-900 mb-2">30-Day Money Back Guarantee</h3>
          <p className="text-green-700 text-sm">
            Not satisfied? Get a full refund within 30 days, no questions asked.
          </p>
        </div>
      </div>

      {/* Security Notice */}
      <div className="mobile-card p-4 mb-6">
        <div className="flex items-center justify-center text-sm text-gray-600">
          <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          Payments secured by Stripe
        </div>
      </div>

      <MobileBottomNav />
    </MobileLayout>
  );
};

export default PaymentPage;